import { useQuery } from "@apollo/client";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import FlashContext from "components/FlashMessages/FlashContext";
import PageLoading from "components/loading/PageLoading";
import useTypedContext from "hooks/useTypedContext";
import useErrorHandle from "hooks/useErrorHandle";
import ViewHeader from "components/ViewHeader";
import ViewHeaderTitle from "components/ViewHeader/Title";
import DocumentationIconButton from "components/DocumentationIconButton";
import useTitle from "hooks/useTitle";
import { AccountContext } from "views/AccountWrapper";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import { getDocsUrl } from "utils/getDocsUrl";
import ViewHeaderNavigation from "components/ViewHeader/Navigation";
import Tab from "ds/components/Tab";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageResources } from "hooks/useAnalytics/pages/resources";
import FeedbackButton from "components/FeedbackButton";
import DesktopView from "components/Mobile/DesktopView";

import { GET_ACCOUNT_STACKS } from "./gql";
import ConfigManagement from "../ConfigManagement";
import IacManagementResourceDetails from "../IacManagement/ResourceDetails";
import ResourcesIacManagement from "./IacManagement";

const ResourcesWrapper = () => {
  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageResources.ResourcesRoot,
  });
  const { onError } = useTypedContext(FlashContext);
  const { pathname } = useLocation();
  const { accountName } = useTypedContext(AccountContext);

  useTitle(`Resources · ${accountName}`);

  const { error, loading, data } = useQuery<{
    hasAnsibleStacks?: boolean;
  }>(GET_ACCOUNT_STACKS, {
    onError,
  });

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading) {
    return <PageLoading />;
  }

  const configurationManagementEnabled = !!data?.hasAnsibleStacks;

  return (
    <>
      <ViewHeader>
        <ViewHeaderTitle>Account resources</ViewHeaderTitle>
        <DesktopView>
          <ViewHeaderWrapper direction="row" align="center">
            {configurationManagementEnabled &&
              pathname === "/resources/configuration-management" && (
                <FeedbackButton id="config-management-in-resources-feedback" />
              )}
            {pathname === "/resources/iac-management" && (
              <FeedbackButton id="iac-management-in-resources-feedback" />
            )}
            <DocumentationIconButton
              href={getDocsUrl("/concepts/resources#account-level-resources")}
              tooltipText="Go to account-level resources documentation"
            />
          </ViewHeaderWrapper>
        </DesktopView>

        {configurationManagementEnabled && (
          <ViewHeaderNavigation>
            <Tab
              id="iac"
              onClick={() => trackSegmentAnalyticsEvent("Tab switched", { tab: "IaC management" })}
              to="/resources/iac-management"
              label="IaC Management"
            />

            <Tab
              id="config-mgmt"
              data-userflow-id="resources-configuration-management"
              to="/resources/configuration-management"
              label="Configuration Management"
              onClick={() =>
                trackSegmentAnalyticsEvent("Tab switched", { tab: "Config management" })
              }
            />
          </ViewHeaderNavigation>
        )}
      </ViewHeader>

      <Routes>
        <Route path="/iac-management" element={<ResourcesIacManagement />}>
          <Route path="resource/:resourceId" element={<IacManagementResourceDetails />} />
        </Route>

        {configurationManagementEnabled && (
          <Route path="/configuration-management" element={<ConfigManagement />} />
        )}

        <Route path="*" element={<Navigate to="/resources/iac-management" replace />} />
      </Routes>
    </>
  );
};

export default ResourcesWrapper;
